import { FC, ReactNode, useMemo, useState } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { SectionCollapsible } from '@dynamika-shared/ui/CollapsibleFormSection/SectionCollapsible';
import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { ExternalUserFile } from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';

interface FileListProps {
  title: string;
  columns: ColumnType<ExternalUserFile>[];
  data: ExternalUserFile[];
  loading: boolean;
  withAction?: ReactNode;
}

const DEFAULT_SORT = 'created,desc';

export const FileList: FC<FileListProps> = ({
  title,
  columns,
  data,
  loading,
  withAction,
}) => {
  const [sortState, setSortState] = useState(DEFAULT_SORT);

  const sortedData = useMemo(() => {
    if (!sortState || !data?.length) return data;

    const [field, order] = sortState.split(',');
    if (field !== 'created') return data;

    return [...data].sort((a, b) => {
      const dateA = a.created ? new Date(a.created).getTime() : 0;
      const dateB = b.created ? new Date(b.created).getTime() : 0;
      return order === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [data, sortState]);

  const handleSort = (column: string, order?: string) => {
    setSortState(order ? `${column},${order}` : '');
  };

  return (
    <SectionCollapsible
      title={<h3 className="text-1824 font-medium">{title}</h3>}
      actions={
        withAction
          ? <div onClick={e => e.stopPropagation()}>{withAction}</div>
          : undefined
      }
      density="tight"
      headerBg="bg-blueTone"
      bodyBg="bg-transparent"
      iconPosition="left"
      defaultOpen={true}
      classNames={{
        container: 'mt-0 mb-1.5',
        header: 'rounded-sm py-1.5 pr-1.5 !h-auto',
        iconBtn: 'border border-brightGray',
        body: 'p-0',
      }}
    >
      <Table
        columns={columns}
        data={sortedData}
        totalItems={sortedData?.length}
        className="!h-auto max-h-[400px]"
        loading={loading}
        sortState={sortState}
        onSort={handleSort}
        styles={{
          th: '[&_:before]:hidden !py-2',
        }}
        noContentMessage={
          <NoContent
            iconName={'noContent'}
            title={
              <span className="text-1424 text-spanishGray">
                <span className="font-semibold text-chineseBlack">
                  No {title.toLowerCase()} for this visit yet
                </span>
              </span>
            }
          >
            <div />
          </NoContent>
        }
      />
    </SectionCollapsible>
  );
};